import { useEffect, useState } from 'react'
import { useReports } from '@/hooks/useReports'
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, Cell, ReferenceLine, Area, AreaChart,
} from 'recharts'
import {
  FileText, TrendingUp, TrendingDown, Shield, Target,
  BarChart2, Download, ChevronLeft, ChevronRight, Loader2, Activity,
  Flame, Snowflake,
  type LucideIcon,
} from 'lucide-react'
import { clsx } from 'clsx'
import PageHeader from '@/components/ui/PageHeader'

// ── Helpers ──────────────────────────────────────────────────────────────────

function pct(n: number) { return `${n >= 0 ? '+' : ''}${n.toFixed(2)}%` }
function usd(n: number) { return `$${n.toLocaleString('en-US', { maximumFractionDigits: 0 })}` }

function KpiCard({ label, value, sub, positive, icon: Icon }: {
  label: string; value: string; sub?: string; positive?: boolean
  icon?: LucideIcon
}) {
  return (
    <div className="stat-card">
      <div className="flex items-center gap-2 mb-1">
        {Icon && <Icon size={14} className="text-text-muted" />}
        <p className="stat-label">{label}</p>
      </div>
      <p className={clsx('stat-value text-xl', positive === true ? 'text-emerald' : positive === false ? 'text-rose-light' : '')}>
        {value}
      </p>
      {sub && <p className="text-xs text-text-muted mt-0.5">{sub}</p>}
    </div>
  )
}

function monthLabel(m: string) {
  const [y, mo] = m.split('-')
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']
  return `${months[parseInt(mo) - 1]} ${y}`
}

function prevMonth(m: string): string {
  const [y, mo] = m.split('-').map(Number)
  if (mo === 1) return `${y - 1}-12`
  return `${y}-${String(mo - 1).padStart(2, '0')}`
}

function nextMonth(m: string): string {
  const [y, mo] = m.split('-').map(Number)
  if (mo === 12) return `${y + 1}-01`
  return `${y}-${String(mo + 1).padStart(2, '0')}`
}

// ── Page ────────────────────────────────────────────────────────────────────

// ── Monthly returns heatmap ──────────────────────────────────────────────────

function MonthlyHeatmap({ data }: { data: import('@/lib/types').MonthlyReturnEntry[] }) {
  if (!data.length) return <p className="text-sm text-text-muted py-4 text-center">No data yet</p>

  const years = [...new Set(data.map(d => d.year))].sort()
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']

  function cell(year: number, monthNum: number) {
    const entry = data.find(d => d.year === year && d.month_num === monthNum)
    if (!entry) return <td key={monthNum} className="w-14 h-9" />
    const v = entry.return_pct
    const intensity = Math.min(Math.abs(v) / 8, 1)   // saturate at ±8 %
    const bg = v >= 0
      ? `rgba(16,185,129,${0.15 + intensity * 0.7})`
      : `rgba(239,68,68,${0.15 + intensity * 0.7})`
    return (
      <td
        key={monthNum}
        title={`${entry.month_name} ${year}: ${v >= 0 ? '+' : ''}${v.toFixed(2)}%`}
        className="w-14 h-9 text-center text-xs font-mono rounded cursor-default select-none transition-opacity hover:opacity-80"
        style={{ background: bg, color: '#fff' }}
      >
        {v >= 0 ? '+' : ''}{v.toFixed(1)}
      </td>
    )
  }

  return (
    <div className="overflow-x-auto">
      <table className="text-xs border-separate border-spacing-1">
        <thead>
          <tr>
            <th className="text-text-muted text-left pr-3 font-normal w-12">Year</th>
            {months.map(m => (
              <th key={m} className="text-text-muted font-normal w-14 text-center">{m}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {years.map(y => (
            <tr key={y}>
              <td className="text-text-secondary pr-3 font-mono">{y}</td>
              {Array.from({ length: 12 }, (_, i) => cell(y, i + 1))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// ── Win/loss streak badge ────────────────────────────────────────────────────

function StreakBadge({ streak }: { streak: import('@/lib/types').WinLossStreak }) {
  const isWin  = streak.streak_type === 'win'
  const isLoss = streak.streak_type === 'loss'
  return (
    <div className="flex items-center gap-4 flex-wrap">
      <div className={clsx(
        'flex items-center gap-2 px-4 py-2 rounded-lg font-semibold text-sm',
        isWin  ? 'bg-emerald/10 text-emerald border border-emerald/30' :
        isLoss ? 'bg-rose/10 text-rose-light border border-rose/30' :
                 'bg-surface-card text-text-secondary border border-border',
      )}>
        {isWin  ? <Flame size={16} /> : isLoss ? <Snowflake size={16} /> : null}
        {streak.streak_days > 0
          ? `${streak.streak_days}-day ${isWin ? 'win' : isLoss ? 'loss' : ''} streak`
          : 'No active streak'}
      </div>
      <div className="text-sm text-text-secondary">
        <span className="text-emerald font-mono">{streak.positive_days}W</span>
        <span className="mx-1 text-text-muted">/</span>
        <span className="text-rose-light font-mono">{streak.negative_days}L</span>
        <span className="ml-2 text-text-muted">({(streak.positive_pct ?? 0).toFixed(1)}% positive)</span>
      </div>
    </div>
  )
}

// ── Page ────────────────────────────────────────────────────────────────────

export default function ReportsPage() {
  const {
    monthly, summary, attribution,
    performanceSeries, rollingSharpe, rollingDrawdown, monthlyReturns, winLossStreak,
    loading, loadingSummary, loadingAttribution, loadingPerformance,
    error, selectedMonth,
    fetchMonthly, fetchSummary, fetchAttribution, fetchPerformanceData, openExport, setMonth,
  } = useReports()

  const [tab, setTab] = useState<'monthly' | 'summary' | 'attribution' | 'performance'>('monthly')
  const [exporting, setExporting] = useState(false)
  const [navDays, setNavDays] = useState(90)
  const [sharpeWindow, setSharpeWindow] = useState(30)

  useEffect(() => {
    fetchMonthly()
    fetchSummary()
    fetchAttribution()
    fetchPerformanceData(navDays, sharpeWindow)
  }, [fetchMonthly, fetchSummary, fetchAttribution, fetchPerformanceData]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleMonthChange = (dir: 'prev' | 'next') => {
    const m = dir === 'prev' ? prevMonth(selectedMonth) : nextMonth(selectedMonth)
    setMonth(m)
    fetchMonthly(m)
  }

  const handleExport = async () => {
    setExporting(true)
    await openExport()
    setExporting(false)
  }

  const handleNavDaysChange = (d: number) => {
    setNavDays(d)
    fetchPerformanceData(d, sharpeWindow)
  }

  const handleSharpeWindowChange = (w: number) => {
    setSharpeWindow(w)
    fetchPerformanceData(navDays, w)
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Performance Reports"
        icon={FileText}
        iconClassName="text-blue"
        subtitle="Hedge fund style investor reporting"
        actions={
          <button
            type="button"
            onClick={handleExport}
            disabled={exporting}
            title="Open print-ready HTML report in a new tab (Ctrl+P to save as PDF)"
            className="btn-primary"
          >
            {exporting ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
            Export Report
          </button>
        }
      />

      {/* Tabs */}
      <div className="flex gap-1 bg-surface-card rounded-lg p-1 flex-wrap">
        {([
          ['monthly',     'Monthly Report'],
          ['summary',     'Fund Summary'],
          ['attribution', 'Attribution'],
          ['performance', 'Performance'],
        ] as const).map(([t, label]) => (
          <button
            type="button"
            key={t}
            onClick={() => setTab(t)}
            className={clsx(
              'px-4 py-2 rounded-md text-sm font-medium transition-colors',
              tab === t ? 'bg-blue text-white' : 'text-text-secondary hover:text-text-primary',
            )}
          >
            {label}
          </button>
        ))}
      </div>

      {error && (
        <div className="bg-rose/10 border border-rose/30 rounded-lg px-4 py-3 text-sm text-rose-light">
          {error}
        </div>
      )}

      {/* Monthly Report Tab */}
      {tab === 'monthly' && (
        <div className="space-y-6">
          {/* Month selector */}
          <div className="flex items-center justify-center gap-4">
            <button
              type="button"
              title="Previous month"
              onClick={() => handleMonthChange('prev')}
              className="p-2 rounded-lg hover:bg-surface-card transition-colors"
            >
              <ChevronLeft size={20} className="text-text-secondary" />
            </button>
            <span className="text-lg font-semibold text-text-primary min-w-[140px] text-center">
              {monthLabel(selectedMonth)}
            </span>
            <button
              type="button"
              title="Next month"
              onClick={() => handleMonthChange('next')}
              className="p-2 rounded-lg hover:bg-surface-card transition-colors"
            >
              <ChevronRight size={20} className="text-text-secondary" />
            </button>
          </div>

          {loading ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="skeleton h-24 rounded-xl" />
                ))}
              </div>
              <div className="skeleton h-64 rounded-xl" />
            </div>
          ) : monthly ? (
            <>
              {/* KPI Row */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 stagger-children">
                <KpiCard label="Fund Return" value={pct(monthly.total_return_pct)} positive={monthly.total_return_pct >= 0} icon={TrendingUp} />
                <KpiCard label="S&P 500" value={pct(monthly.benchmark_return_pct)} sub="Benchmark" icon={BarChart2} />
                <KpiCard label="Alpha" value={pct(monthly.alpha_pct)} positive={monthly.alpha_pct >= 0} icon={Target} />
                <KpiCard label="AUM" value={usd(monthly.aum)} icon={Activity} />
              </div>

              {/* Risk & Exposure Row */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 stagger-children">
                <KpiCard label="Sharpe Ratio" value={(monthly.sharpe_ratio ?? 0).toFixed(2)} icon={Shield} />
                <KpiCard label="Sortino Ratio" value={(monthly.sortino_ratio ?? 0).toFixed(2)} icon={Shield} />
                <KpiCard label="Max Drawdown" value={`-${(monthly.max_drawdown_pct ?? 0).toFixed(2)}%`} positive={false} icon={TrendingDown} />
                <KpiCard label="Volatility" value={`${(monthly.volatility_pct ?? 0).toFixed(1)}%`} />
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 stagger-children">
                <KpiCard label="Net Exposure" value={`${(monthly.net_exposure_pct ?? 0).toFixed(1)}%`} />
                <KpiCard label="Gross Exposure" value={`${(monthly.gross_exposure_pct ?? 0).toFixed(1)}%`} />
                <KpiCard label="Win Rate" value={`${(monthly.win_rate ?? 0).toFixed(1)}%`} positive={(monthly.win_rate ?? 0) >= 50} />
                <KpiCard label="Total P&L" value={usd(monthly.total_pnl)} positive={monthly.total_pnl >= 0} />
              </div>

              {/* Best / Worst Trades */}
              <div className="grid md:grid-cols-2 gap-4">
                <div className="card p-4">
                  <h3 className="panel-title mb-3 flex items-center gap-2">
                    <TrendingUp size={14} className="text-emerald" /> Best Trades
                  </h3>
                  <div className="space-y-2">
                    {monthly.best_trades.map((t, i) => (
                      <div key={i} className="flex items-center justify-between py-2 border-b border-border last:border-0">
                        <div>
                          <span className="font-mono font-semibold text-text-primary">{t.ticker}</span>
                          <span className="ml-2 text-xs text-text-muted">{t.action}</span>
                        </div>
                        <div className="text-right">
                          <span className="text-emerald font-mono text-sm">{pct(t.pnl_pct)}</span>
                          <span className="ml-2 text-xs text-text-muted">{usd(t.pnl)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="card p-4">
                  <h3 className="panel-title mb-3 flex items-center gap-2">
                    <TrendingDown size={14} className="text-rose-light" /> Worst Trades
                  </h3>
                  <div className="space-y-2">
                    {monthly.worst_trades.map((t, i) => (
                      <div key={i} className="flex items-center justify-between py-2 border-b border-border last:border-0">
                        <div>
                          <span className="font-mono font-semibold text-text-primary">{t.ticker}</span>
                          <span className="ml-2 text-xs text-text-muted">{t.action}</span>
                        </div>
                        <div className="text-right">
                          <span className="text-rose-light font-mono text-sm">{pct(t.pnl_pct)}</span>
                          <span className="ml-2 text-xs text-text-muted">{usd(t.pnl)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Sector Attribution Chart */}
              {monthly.sector_attribution.length > 0 && (
                <div className="card p-4">
                  <h3 className="panel-title mb-4">Sector Attribution</h3>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={monthly.sector_attribution} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" stroke="#2a2a40" />
                        <XAxis type="number" tick={{ fill: '#888', fontSize: 12 }} />
                        <YAxis
                          dataKey="sector"
                          type="category"
                          width={120}
                          tick={{ fill: '#ccc', fontSize: 12 }}
                        />
                        <Tooltip
                          contentStyle={{ background: '#1a1a2e', border: '1px solid #333', borderRadius: '8px', color: 'rgb(var(--text-primary))'}}
                          labelStyle={{ color: '#ccc' }}
                          formatter={(value: number) => `${value.toFixed(1)}%`}
                        />
                        <Bar dataKey="contribution_pct" name="Contribution %">
                          {monthly.sector_attribution.map((s, i) => (
                            <Cell
                              key={i}
                              fill={s.contribution_pct >= 0 ? '#10b981' : '#ef4444'}
                            />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}

              {/* Agent Performance Table */}
              {monthly.agent_performance.length > 0 && (
                <div className="card p-4">
                  <h3 className="panel-title mb-4">Agent Performance</h3>
                  <div className="overflow-x-auto">
                    <table className="table-pro w-full text-sm">
                      <thead>
                        <tr className="text-text-muted text-xs uppercase border-b border-border">
                          <th className="text-left py-2 px-3">Agent</th>
                          <th className="text-right py-2 px-3">Trades</th>
                          <th className="text-right py-2 px-3">Win Rate</th>
                          <th className="text-right py-2 px-3">Avg Return</th>
                          <th className="text-right py-2 px-3">Total P&L</th>
                        </tr>
                      </thead>
                      <tbody>
                        {monthly.agent_performance.map((a) => (
                          <tr key={a.agent_id} className="border-b border-border/50 hover:bg-surface-card/50">
                            <td className="py-2 px-3 font-medium text-text-primary">{a.agent_name}</td>
                            <td className="py-2 px-3 text-right text-text-secondary">{a.trade_count}</td>
                            <td className="py-2 px-3 text-right">
                              <span className={a.win_rate >= 50 ? 'text-emerald' : 'text-rose-light'}>
                                {(a.win_rate ?? 0).toFixed(1)}%
                              </span>
                            </td>
                            <td className="py-2 px-3 text-right">
                              <span className={a.avg_return_pct >= 0 ? 'text-emerald' : 'text-rose-light'}>
                                {pct(a.avg_return_pct)}
                              </span>
                            </td>
                            <td className="py-2 px-3 text-right font-mono">
                              <span className={a.total_pnl >= 0 ? 'text-emerald' : 'text-rose-light'}>
                                {(a.total_pnl ?? 0) >= 0 ? '+' : ''}{(a.total_pnl ?? 0).toFixed(2)}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          ) : null}
        </div>
      )}

      {/* Fund Summary Tab */}
      {tab === 'summary' && (
        <div className="space-y-6">
          {loadingSummary ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="skeleton h-24 rounded-xl" />
                ))}
              </div>
              <div className="skeleton h-64 rounded-xl" />
            </div>
          ) : summary ? (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 stagger-children">
                <KpiCard label="Inception" value={summary.inception_date} icon={FileText} />
                <KpiCard label="Total Return" value={pct(summary.total_return_pct)} positive={summary.total_return_pct >= 0} icon={TrendingUp} />
                <KpiCard label="Annualized" value={pct(summary.annualized_return_pct)} positive={summary.annualized_return_pct >= 0} icon={Target} />
                <KpiCard label="AUM" value={usd(summary.aum)} icon={Activity} />
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 stagger-children">
                <KpiCard label="Sharpe Ratio" value={(summary.sharpe_ratio ?? 0).toFixed(2)} icon={Shield} />
                <KpiCard label="Sortino Ratio" value={(summary.sortino_ratio ?? 0).toFixed(2)} icon={Shield} />
                <KpiCard label="Max Drawdown" value={`-${(summary.max_drawdown_pct ?? 0).toFixed(2)}%`} positive={false} icon={TrendingDown} />
                <KpiCard label="Win Rate" value={`${(summary.win_rate ?? 0).toFixed(1)}%`} positive={(summary.win_rate ?? 0) >= 50} />
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 stagger-children">
                <KpiCard label="Profit Factor" value={(summary.profit_factor ?? 0).toFixed(2)} positive={(summary.profit_factor ?? 0) >= 1} />
                <KpiCard label="Total Trades" value={String(summary.total_trades)} />
              </div>

              {/* AUM History Chart */}
              {summary.aum_history.length > 0 && (
                <div className="card p-4">
                  <h3 className="panel-title mb-4">AUM History</h3>
                  <div className="h-72">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={summary.aum_history}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#2a2a40" />
                        <XAxis
                          dataKey="date"
                          tick={{ fill: '#888', fontSize: 11 }}
                          tickFormatter={(d: string) => d.slice(5)}
                        />
                        <YAxis
                          tick={{ fill: '#888', fontSize: 11 }}
                          tickFormatter={(v: number) => `£${(v / 1000).toFixed(0)}k`}
                        />
                        <Tooltip
                          contentStyle={{ background: '#1a1a2e', border: '1px solid #333', borderRadius: '8px', color: 'rgb(var(--text-primary))'}}
                          labelStyle={{ color: '#ccc' }}
                          formatter={(value: number) => [`£${value.toLocaleString('en-GB')}`, 'AUM']}
                        />
                        <Line
                          type="monotone"
                          dataKey="aum"
                          stroke="#3b82f6"
                          strokeWidth={2}
                          dot={false}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}
            </>
          ) : null}
        </div>
      )}

      {/* Attribution Tab */}
      {tab === 'attribution' && (
        <div className="space-y-6">
          {loadingAttribution ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="skeleton h-24 rounded-xl" />
                ))}
              </div>
              <div className="skeleton h-64 rounded-xl" />
            </div>
          ) : attribution ? (
            <>
              {[
                { title: 'By Sector', data: attribution.by_sector },
                { title: 'By Agent', data: attribution.by_agent },
                { title: 'By Strategy', data: attribution.by_strategy },
                { title: 'By Signal Source', data: attribution.by_signal_source },
              ].map(({ title, data }) => (
                <div key={title} className="card p-4">
                  <h3 className="panel-title mb-4">{title}</h3>
                  {data.length > 0 ? (
                    <>
                      <div className="h-48">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={data} layout="vertical">
                            <CartesianGrid strokeDasharray="3 3" stroke="#2a2a40" />
                            <XAxis type="number" tick={{ fill: '#888', fontSize: 12 }} />
                            <YAxis
                              dataKey="label"
                              type="category"
                              width={130}
                              tick={{ fill: '#ccc', fontSize: 12 }}
                            />
                            <Tooltip
                              contentStyle={{ background: '#1a1a2e', border: '1px solid #333', borderRadius: '8px', color: 'rgb(var(--text-primary))'}}
                              labelStyle={{ color: '#ccc' }}
                              formatter={(value: number) => `${value.toFixed(1)}%`}
                            />
                            <Bar dataKey="contribution_pct" name="Contribution %">
                              {data.map((d, i) => (
                                <Cell
                                  key={i}
                                  fill={d.contribution_pct >= 0 ? '#10b981' : '#ef4444'}
                                />
                              ))}
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                      <div className="mt-4 overflow-x-auto">
                        <table className="table-pro w-full text-sm">
                          <thead>
                            <tr className="text-text-muted text-xs uppercase border-b border-border">
                              <th className="text-left py-2 px-3">Source</th>
                              <th className="text-right py-2 px-3">P&L</th>
                              <th className="text-right py-2 px-3">Contribution</th>
                              <th className="text-right py-2 px-3">Trades</th>
                            </tr>
                          </thead>
                          <tbody>
                            {data.map((d) => (
                              <tr key={d.label} className="border-b border-border/50">
                                <td className="py-2 px-3 text-text-primary">{d.label}</td>
                                <td className={clsx('py-2 px-3 text-right font-mono', d.pnl >= 0 ? 'text-emerald' : 'text-rose-light')}>
                                  ${(d.pnl ?? 0).toFixed(2)}
                                </td>
                                <td className="py-2 px-3 text-right text-text-secondary">
                                  {(d.contribution_pct ?? 0) >= 0 ? '+' : ''}{(d.contribution_pct ?? 0).toFixed(1)}%
                                </td>
                                <td className="py-2 px-3 text-right text-text-secondary">{d.trade_count}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </>
                  ) : (
                    <p className="text-sm text-text-muted py-4 text-center">No data available</p>
                  )}
                </div>
              ))}
            </>
          ) : null}
        </div>
      )}

      {/* Performance Tab */}
      {tab === 'performance' && (
        <div className="space-y-6">
          {loadingPerformance ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="skeleton h-24 rounded-xl" />
                ))}
              </div>
              <div className="skeleton h-64 rounded-xl" />
            </div>
          ) : (
            <>
              {/* Win / Loss Streak */}
              {winLossStreak && (
                <div className="card p-4">
                  <h3 className="panel-title mb-3 flex items-center gap-2">
                    <Flame size={14} className="text-amber-400" /> Win / Loss Streak
                  </h3>
                  <StreakBadge streak={winLossStreak} />
                </div>
              )}

              {/* NAV Series chart */}
              {performanceSeries.length > 0 && (
                <div className="card p-4">
                  <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                    <h3 className="panel-title">Portfolio NAV (indexed to 100)</h3>
                    <div className="flex gap-1">
                      {[30, 90, 180, 365].map(d => (
                        <button
                          type="button"
                          key={d}
                          onClick={() => handleNavDaysChange(d)}
                          className={clsx(
                            'px-2 py-1 rounded text-xs font-mono transition-colors',
                            navDays === d ? 'bg-blue text-white' : 'text-text-muted hover:text-text-primary bg-surface-card',
                          )}
                        >
                          {d}d
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="h-72">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={performanceSeries}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#2a2a40" />
                        <XAxis
                          dataKey="date"
                          tick={{ fill: '#888', fontSize: 11 }}
                          tickFormatter={(d: string) => d.slice(5)}
                          interval="preserveStartEnd"
                        />
                        <YAxis
                          tick={{ fill: '#888', fontSize: 11 }}
                          tickFormatter={(v: number) => v.toFixed(0)}
                          domain={['auto', 'auto']}
                        />
                        <Tooltip
                          contentStyle={{ background: '#1a1a2e', border: '1px solid #333', borderRadius: '8px', color: 'rgb(var(--text-primary))'}}
                          labelStyle={{ color: '#ccc' }}
                          formatter={(value: number, name: string) => [
                            name === 'nav_indexed' ? value.toFixed(2) : `$${value.toLocaleString()}`,
                            name === 'nav_indexed' ? 'NAV' : name,
                          ]}
                        />
                        <ReferenceLine y={100} stroke="#444" strokeDasharray="4 2" />
                        <Line
                          type="monotone"
                          dataKey="nav_indexed"
                          stroke="#3b82f6"
                          strokeWidth={2}
                          dot={false}
                          name="nav_indexed"
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}

              {/* Rolling Sharpe chart */}
              {rollingSharpe.length > 0 && (
                <div className="card p-4">
                  <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                    <h3 className="panel-title flex items-center gap-2">
                      <Shield size={14} className="text-blue" /> Rolling Sharpe Ratio
                    </h3>
                    <div className="flex gap-1">
                      {[14, 30, 60, 90].map(w => (
                        <button
                          type="button"
                          key={w}
                          onClick={() => handleSharpeWindowChange(w)}
                          className={clsx(
                            'px-2 py-1 rounded text-xs font-mono transition-colors',
                            sharpeWindow === w ? 'bg-blue text-white' : 'text-text-muted hover:text-text-primary bg-surface-card',
                          )}
                        >
                          {w}d
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="h-56">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={rollingSharpe}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#2a2a40" />
                        <XAxis
                          dataKey="date"
                          tick={{ fill: '#888', fontSize: 11 }}
                          tickFormatter={(d: string) => d.slice(5)}
                          interval="preserveStartEnd"
                        />
                        <YAxis tick={{ fill: '#888', fontSize: 11 }} domain={['auto', 'auto']} />
                        <Tooltip
                          contentStyle={{ background: '#1a1a2e', border: '1px solid #333', borderRadius: '8px', color: 'rgb(var(--text-primary))'}}
                          labelStyle={{ color: '#ccc' }}
                          formatter={(v: number) => [v.toFixed(3), 'Sharpe']}
                        />
                        <ReferenceLine y={0} stroke="#666" strokeDasharray="3 2" />
                        <ReferenceLine y={1} stroke="#10b981" strokeDasharray="3 2" label={{ value: '1.0', fill: '#10b981', fontSize: 10 }} />
                        <Line
                          type="monotone"
                          dataKey="sharpe"
                          stroke="#a78bfa"
                          strokeWidth={2}
                          dot={false}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}

              {/* Drawdown chart */}
              {rollingDrawdown.length > 0 && (
                <div className="card p-4">
                  <h3 className="panel-title mb-4 flex items-center gap-2">
                    <TrendingDown size={14} className="text-rose-light" /> Drawdown from Peak
                  </h3>
                  <div className="h-56">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={rollingDrawdown}>
                        <defs>
                          <linearGradient id="ddGrad" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%"  stopColor="#ef4444" stopOpacity={0.3} />
                            <stop offset="95%" stopColor="#ef4444" stopOpacity={0.02} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#2a2a40" />
                        <XAxis
                          dataKey="date"
                          tick={{ fill: '#888', fontSize: 11 }}
                          tickFormatter={(d: string) => d.slice(5)}
                          interval="preserveStartEnd"
                        />
                        <YAxis
                          tick={{ fill: '#888', fontSize: 11 }}
                          tickFormatter={(v: number) => `${v.toFixed(1)}%`}
                          domain={['auto', 0]}
                        />
                        <Tooltip
                          contentStyle={{ background: '#1a1a2e', border: '1px solid #333', borderRadius: '8px', color: 'rgb(var(--text-primary))'}}
                          labelStyle={{ color: '#ccc' }}
                          formatter={(v: number) => [`${v.toFixed(2)}%`, 'Drawdown']}
                        />
                        <ReferenceLine y={0} stroke="#444" />
                        <Area
                          type="monotone"
                          dataKey="drawdown_pct"
                          stroke="#ef4444"
                          strokeWidth={1.5}
                          fill="url(#ddGrad)"
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}

              {/* Monthly returns heatmap */}
              {monthlyReturns.length > 0 && (
                <div className="card p-4">
                  <h3 className="panel-title mb-4 flex items-center gap-2">
                    <BarChart2 size={14} className="text-blue" /> Monthly Returns Calendar
                  </h3>
                  <MonthlyHeatmap data={monthlyReturns} />
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  )
}
